import { DEFAULT_RULES, RULE_IDS, type BlockId, type RuleId, type Thresholds } from './rules.js';
import type { Alert, Light, Segment } from './types.js';

/** Semáforo de un valor frente a sus umbrales. */
export function lightFor(value: number, t: Thresholds): Light {
  if (t.inv) return value <= t.red ? 'rojo' : value <= t.amber ? 'ambar' : 'verde';
  return value >= t.red ? 'rojo' : value >= t.amber ? 'ambar' : 'verde';
}

export type TableAllocation = 'global' | 'quota' | 'minimum';
/** tiered: segmento A/B → C/D/E, dentro rojo → ámbar, y la puntuación desempata; score: solo puntuación */
export type TableOrdering = 'tiered' | 'score';

/**
 * Pesos de la priorización y del reparto de la mesa. Se calibran en el panel de admin (playground)
 * y se guardan en la BBDD del panel; los valores por defecto son la propuesta del brief.
 */
export interface ScoringConfig {
  /** Puntos base por semáforo */
  lightPoints: { rojo: number; ambar: number };
  /** Puntos por segmento; `none` para comunidades sin segmento */
  segmentPoints: Record<Segment | 'none', number>;
  /** Bonus si la comunidad tiene junta en ≤ `days` días */
  meetingSoon: { days: number; points: number };
  /** `weight` × min(valor / umbral rojo, `cap`) (inversas: umbral rojo / valor) */
  ratio: { weight: number; cap: number };
  /** Puntos extra por regla, para dar más peso a un tipo de caso */
  ruleBonus: Record<RuleId, number>;
  table: {
    /** Máximo de casos en la mesa */
    max: number;
    /**
     * global: top `max` por puntuación;
     * quota: plazas por bloque en proporción a sus minutos (lo que no se use, por puntuación);
     * minimum: top por puntuación garantizando `minPerBlock` casos a cada bloque con alertas.
     */
    allocation: TableAllocation;
    minPerBlock: number;
    /**
     * tiered: los casos A/B van siempre antes que C/D/E (y el reparto se hace nivel a nivel),
     * así la mesa de «Todos» siempre contiene la de «A + B»; dentro de cada nivel, rojo antes que ámbar.
     */
    ordering: TableOrdering;
    /** Un sitio por caso: las reglas de un mismo ticket/junta se agrupan en una fila (la peor manda) */
    groupByCase: boolean;
  };
  /** Minutos de cada bloque en el orden del día (también reparten las plazas en `quota`) */
  blockMinutes: Record<BlockId, number>;
}

export const DEFAULT_SCORING: ScoringConfig = {
  lightPoints: { rojo: 100, ambar: 50 },
  segmentPoints: { A: 30, B: 20, C: 5, D: 0, E: 0, none: 0 },
  meetingSoon: { days: 28, points: 25 },
  ratio: { weight: 10, cap: 3 },
  ruleBonus: Object.fromEntries(RULE_IDS.map((id) => [id, 0])) as Record<RuleId, number>,
  // 16 oct 2026: orden por niveles + cupo por bloque + un sitio por caso (sustituye al top 12 global del brief)
  table: { max: 12, allocation: 'quota', minPerBlock: 2, ordering: 'tiered', groupByCase: true },
  blockMinutes: { J: 20, S: 20, C: 15, F: 15 },
};

/** Completa una configuración guardada (parcial o de una versión anterior) con los valores por defecto. */
export function normalizeScoring(c: Partial<ScoringConfig> | null | undefined): ScoringConfig {
  const d = DEFAULT_SCORING;
  return {
    lightPoints: { ...d.lightPoints, ...c?.lightPoints },
    segmentPoints: { ...d.segmentPoints, ...c?.segmentPoints },
    meetingSoon: { ...d.meetingSoon, ...c?.meetingSoon },
    ratio: { ...d.ratio, ...c?.ratio },
    ruleBonus: { ...d.ruleBonus, ...c?.ruleBonus },
    table: { ...d.table, ...c?.table },
    blockMinutes: { ...d.blockMinutes, ...c?.blockMinutes },
  };
}

/** @deprecated usa `scoring.meetingSoon.days` */
export const MEETING_SOON_DAYS = DEFAULT_SCORING.meetingSoon.days;

/**
 * Priorización (PROPUESTA A VALIDAR del brief, con los pesos de `cfg`):
 * semáforo + segmento + bonus junta próxima + weight × min(ratio, cap) + bonus de regla
 */
export function scoreOf(
  a: {
    value: number;
    segment: Segment | null;
    daysToMeeting: number | null;
    ruleId?: RuleId;
    light?: Light;
  } & Thresholds,
  cfg: ScoringConfig = DEFAULT_SCORING,
): number {
  const manual = a.ruleId === 'M1';
  // Los casos manuales traen su semáforo; el resto se calcula con los umbrales
  const light = manual && a.light ? a.light : lightFor(a.value, a);
  let s = light === 'rojo' ? cfg.lightPoints.rojo : light === 'ambar' ? cfg.lightPoints.ambar : 0;
  s += cfg.segmentPoints[a.segment ?? 'none'] ?? 0;
  if (a.daysToMeeting != null && a.daysToMeeting <= cfg.meetingSoon.days) s += cfg.meetingSoon.points;
  if (!manual) {
    const ratio = a.inv ? a.red / Math.max(a.value, 0.1) : a.value / a.red;
    s += Math.min(ratio, cfg.ratio.cap) * cfg.ratio.weight;
  }
  if (a.ruleId) s += cfg.ruleBonus[a.ruleId] ?? 0;
  return Math.round(s);
}

/** Recalcula la puntuación de las alertas con otra configuración (playground / config guardada). */
export function rescore<T extends Alert>(alerts: T[], cfg: ScoringConfig): T[] {
  return alerts.map((a) => ({ ...a, score: scoreOf(a, cfg) }));
}

export const isTopSegment = (s: Segment | null) => s === 'A' || s === 'B';

/** «Prioridad máx.»: segmento A/B y (rojo o junta próxima). */
export function isMaxPriority(
  a: Pick<Alert, 'segment' | 'light' | 'daysToMeeting'>,
  cfg: ScoringConfig = DEFAULT_SCORING,
): boolean {
  return (
    isTopSegment(a.segment) &&
    (a.light === 'rojo' || (a.daysToMeeting != null && a.daysToMeeting <= cfg.meetingSoon.days))
  );
}

export const blockOf = (ruleId: RuleId): BlockId => DEFAULT_RULES[ruleId].block;
/** Bloque de una alerta: el suyo propio (casos manuales) o el de su regla */
export const blockOfAlert = (a: { ruleId: RuleId; block?: BlockId }): BlockId => a.block ?? blockOf(a.ruleId);
